import moment from 'moment';
import DDLogs from '@/DDLogs';
import * as _ from 'lodash';
import subscriptionStateApi from '../../api/SubscriptionStateApi';
import billyApi from '../../api/BillyApi';

import { TRIAL_PERIODS, SUBSCRIPTION_STATES, SUBSCRIPTION_TYPES } from '../../constants/subscription-state';

const LOCAL_STORAGE_KEY = 'riseup.subscriptionCtaDismissedData';
const LOCAL_STORAGE_KEY_MBG = 'riseup.subscriptionMbgCtaRemindMe';

const hoursToShowMbgOffer = 24 * 2;
const hoursForDismissButton = 12;

const state = {
  subscriptionState: null,
  since: null,
  until: null,
  untilWithPromo: null,
  newPromotionsTest: null,
  nextPaymentDate: null,
  type: null,
  moneyBackGuaranteeRecord: null,
};

function _shouldShowCta({ inTrial, minDaysPassedToShowCta, trialPeriod }) {
  if (inTrial && minDaysPassedToShowCta) {
    const subscriptionDismissedDataString = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (subscriptionDismissedDataString) {
      const { periodDismissed } = JSON.parse(subscriptionDismissedDataString);
      if (!periodDismissed) {
        return true;
      }
      const currentTrialPeriod = trialPeriod;
      return currentTrialPeriod !== periodDismissed;
    }
    return true;
  }
  return false;
}

function _hasMoneyBackOffer({ response, inTrial, mbgOfferEndDate }) {
  return !_.isEmpty(response)
    && (_.isNil(response.acceptedOffer))
    && inTrial
    && moment().isBefore(mbgOfferEndDate);
}

function _mbgCtaDismissed() {
  const remindMeLaterInput = localStorage.getItem(LOCAL_STORAGE_KEY_MBG);
  if (!remindMeLaterInput) {
    return false;
  }
  const { dismissDate } = JSON.parse(remindMeLaterInput);
  if (!dismissDate) {
    return false;
  }
  return _dismissedRecently(dismissDate);
}

function _dismissedRecently(dismissDate) {
  return moment().diff(moment(dismissDate), 'hours') < hoursForDismissButton;
}

const getters = {
  daysLeft: state => Math.max(moment(state.until).diff(moment(), 'days') + 1, 0),
  daysLeftIncludingPromotion: state => Math.max(moment(state.untilWithPromo).diff(moment(), 'days') + 1, 0),
  trialLength: state => moment(state.until).diff(moment(state.since), 'days'),
  trialPeriod: (state, getters) => {
    if (getters.inTrial) {
      const diffWithUntil = moment(state.until).diff(moment(), 'days');
      if (diffWithUntil > 20) {
        return TRIAL_PERIODS.BEGINNING;
      } if (diffWithUntil <= 20 && diffWithUntil > 7) {
        return TRIAL_PERIODS.MIDDLE;
      }
      // 7 days left for the trial
      return TRIAL_PERIODS.FINAL;
    }
    return undefined;
  },
  inTrial: state => state.subscriptionState === SUBSCRIPTION_STATES.TRIAL,
  subscribed: state => state.subscriptionState === SUBSCRIPTION_STATES.SUBSCRIBED,
  canceled: state => state.subscriptionState === SUBSCRIPTION_STATES.CANCELED,
  minLeftDaysToPresentFirstCtaForTestCustomers: () => 35,
  mbgOfferEndDate: state => {
    if (state.moneyBackGuaranteeRecord) {
      return moment(state.moneyBackGuaranteeRecord.created).add(hoursToShowMbgOffer, 'hour');
    }
    return null;
  },
  shouldShowCta: (state, getters) => _shouldShowCta({
    inTrial: getters.inTrial,
    minDaysPassedToShowCta: getters.daysLeft <= getters.minLeftDaysToPresentFirstCtaForTestCustomers,
    trialPeriod: getters.trialPeriod,
  }),
  hasMoneyBackOffer: (state, getters) => _hasMoneyBackOffer({
    response: state.moneyBackGuaranteeRecord,
    inTrial: getters.inTrial,
    mbgOfferEndDate: getters.mbgOfferEndDate,
  }),
  showMbgPopup: () => !_mbgCtaDismissed(),
  isYearlySubscription: state => state.type === SUBSCRIPTION_TYPES.YEARLY,
  isDormant: state => _.includes([SUBSCRIPTION_STATES.DORMANT_AFTER_TRIAL, SUBSCRIPTION_STATES.DORMANT_AFTER_CANCELED], state.subscriptionState),
};

const actions = {
  async initSubscriptionState({ dispatch }) {
    await dispatch('fetchSubscriptionState');
  },
  async fetchSubscriptionState({ commit }) {
    const subscriptionState = await subscriptionStateApi.fetchSubscriptionState();
    const moneyBackGuaranteeRecord = await subscriptionStateApi.fetchMoneyBackGuaranteeRecord();
    commit('setSubscriptionState', subscriptionState.state);
    commit('setSince', subscriptionState.since);
    commit('setUntil', subscriptionState.until);
    commit('setUntilWithPromo', subscriptionState.untilWithPromo);
    commit('setNewPromotionsTest', subscriptionState.newPromotionsTest);
    commit('setMoneyBackGuaranteeRecord', moneyBackGuaranteeRecord);
  },
  async fetchNextPaymentDate({ commit }) {
    try {
      const { subscription } = await billyApi.getSubscription();
      commit('setNextPaymentDate', subscription.nextPaymentDate);
      commit('setType', subscription.iterationType);
    } catch (error) {
      commit('setNextPaymentDate', undefined);
    }
  },
  ctaDismissed({ getters }) {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({ dismissDate: new Date(), periodDismissed: getters.trialPeriod }));
    } catch (error) {
      DDLogs.error(error);
    }
  },
  ctaDismissedMbg({ getters }) {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY_MBG, JSON.stringify({ dismissDate: new Date() }));
    } catch (error) {
      DDLogs.error(error);
    }
  },
};

// mutations
const mutations = {
  setSubscriptionState(state, subscriptionState) {
    state.subscriptionState = subscriptionState;
  },
  setSince(state, since) {
    state.since = since;
  },
  setUntil(state, until) {
    state.until = until;
  },
  setNextPaymentDate(state, nextPaymentDate) {
    state.nextPaymentDate = nextPaymentDate;
  },
  setUntilWithPromo(state, untilWithPromo) {
    state.untilWithPromo = untilWithPromo;
  },
  setNewPromotionsTest(state, newPromotionsTest) {
    state.newPromotionsTest = newPromotionsTest;
  },
  setMoneyBackGuaranteeRecord(state, response) {
    state.moneyBackGuaranteeRecord = response;
  },
  setType(state, type) {
    state.type = type;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
